import React from "react"
import {
  EcommerceIntegration,
  EcommerceIntegrationStatus,
  ECommercePlatforms,
} from "@/types/ecommerce"
import { useTranslation } from "react-i18next"
import { deleteEcommerceShop, postEcommerceAuthenticate } from "@/api/ecommerce"
import {
  ErrorNotification,
  SuccessNotification,
} from "@/UI/Notifications/NotificationTemplate.component"
import { Card, message, Tooltip } from "antd"
import { MediumText } from "@/UI"
import { faTrash, faWarning } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ConfirmModal from "@/UI/Modals/ConfirmModal"
import { useFlags } from "flagsmith/react"
import KosmoButton from "@/UI/Buttons/KosmoButton"

interface EcommerceShopsListProps {
  ecommerceIntegrations: EcommerceIntegration[]
  setEcommerceIntegrations: (ecommerceIntegrations: EcommerceIntegration[]) => void
  setStoreFocus: any
}

const EcommerceShopsList: React.FC<EcommerceShopsListProps> = ({
  ecommerceIntegrations,
  setEcommerceIntegrations,
  setStoreFocus,
}) => {
  const { t } = useTranslation()

  const flags = useFlags(["show_platform_delivery_methods_settings"])

  const deliverySettingsFlags = JSON.parse(
    (flags.show_platform_delivery_methods_settings.value as string) || "{}"
  )

  const onDelete = async (platform: ECommercePlatforms, id: string) => {
    try {
      await deleteEcommerceShop(platform, id)
      SuccessNotification(
        t("profile.EcommerceIntegration.deleteIntegration"),
        t("profile.EcommerceIntegration.deleteIntegrationSuccess")
      )
      const filteredIntegrations = [...ecommerceIntegrations].filter(
        (integration) => integration.id !== id
      )
      setEcommerceIntegrations(filteredIntegrations)
    } catch (error) {
      ErrorNotification(
        t("profile.EcommerceIntegration.deleteIntegration"),
        t("profile.EcommerceIntegration.deleteIntegrationFailed")
      )
    }
  }

  const onUpdateStore = async (platform: ECommercePlatforms, username: string, name: string) => {
    try {
      const { data } = await postEcommerceAuthenticate({
        request: {
          platform,
          username,
          callbackURL: `${
            process.env.NEXT_PUBLIC_APP_HOST || "https://app.kosmo.delivery"
          }/ecommerce/validate`,
        },
        name,
      })
      window.open(data.redirectURL, "_blank", "noreferrer")
    } catch (error) {
      message.error(t("profile.EcommerceShopsList.errorUpdatingAuthStore"))
    }
  }

  return (
    <>
      {ecommerceIntegrations.map((ecommerceIntegration) => {
        const isActive = ecommerceIntegration.status === EcommerceIntegrationStatus.ACTIVE
        return (
          <Card
            className={`${!isActive ? "border-red-400" : ""}`}
            styles={{ body: { padding: "1em" } }}
          >
            <div className="grid grid-cols-12" id="ecommerce-shop-card">
              <div className="col-span-9">
                <div className="flex space-x-3 items-center">
                  <MediumText className="font-semibold">
                    {t("profile.EcommerceSettings.shopName")}
                  </MediumText>
                  <p className="text-[#70747D] font-normal">
                    {ecommerceIntegration.name || "Shopify store name"}
                  </p>
                  {!isActive && (
                    <Tooltip title={t("common.updateRequired")} placement="right">
                      <FontAwesomeIcon icon={faWarning} color="#ef4444" />
                    </Tooltip>
                  )}
                </div>
                {deliverySettingsFlags[ecommerceIntegration.platform] && (
                  <p
                    className="text-kosmoPrimaryBlue w-fit cursor-pointer underline underline-offset-4"
                    onClick={() => setStoreFocus(ecommerceIntegration)}
                    id="shop-settings-link"
                  >
                    {t("profile.EcommerceSettings.shopSettings")}
                  </p>
                )}
              </div>
              <div className="col-span-3 flex justify-end items-center gap-2">
                {!isActive && (
                  <KosmoButton
                    type="default"
                    onClick={() =>
                      onUpdateStore(
                        ecommerceIntegration.platform,
                        ecommerceIntegration?.data?.shop,
                        ecommerceIntegration.name
                      )
                    }
                  >
                    {t("profile.EcommerceShopsList.updateShop")}
                  </KosmoButton>
                )}
                <ConfirmModal
                  button={
                    <div className="flex cursor-pointer item-center bg-[#F3F4F6] h-fit w-fit rounded-full p-2">
                      <FontAwesomeIcon className="text-gray-400" icon={faTrash} />
                    </div>
                  }
                  content={{
                    type: "danger",
                    title: t("profile.EcommerceSettings.deleteEcommerceStore"),
                    description: t("profile.EcommerceSettings.deleteEcommerceStoreConfirmation"),
                    cancelText: t("common.cancel"),
                    okText: t("common.yesDelete"),
                  }}
                  onConfirm={() => onDelete(ecommerceIntegration.platform, ecommerceIntegration.id)}
                />
              </div>
            </div>
          </Card>
        )
      })}
    </>
  )
}

export default EcommerceShopsList
